import { ReactNode, useCallback, Ref, useRef, MouseEvent, useMemo, useState } from 'react';

import { Text, Box, Flex, Paragraph, rem, useColorMode, Button, Collapse } from '@vimeo/bokeh';
import { ChevronUpSmall, ChevronDownSmall, PinOnFilled } from '@vimeo/bokeh-icons';
import { IBaseDomElementProps } from '@vimeo/core/types/dom';

import { NewQuestionItemReply } from './NewQuestionItemReply';
import {
  createQuestionInfoStyle,
  TIME_ASKED_STYLE,
  createQuestionTextStyle,
  createQuestionItemWrapperStyle,
  createAvatarWrapperStyle,
} from './QuestionItem.styles';
import { QuestionItemActions } from './QuestionItemActions';
import { QuestionItemAuthor } from './QuestionItemAuthor';
import { QuestionItemVotesCount } from './QuestionItemVotesCount';
import { useQuestionTimeAsked } from './useQuestionTimeAsked';
import { default as Avatar } from '../../../../components/illustrations/Avatar.svg';
import { useSourceHoverControls } from '../../../../hooks/live/useSourceHoverControls';
import {
  trackDeleteQuestionReply,
  trackSelectQuestion,
} from '../../../../tracking/tracking_engagement_qna';
import { T_ANONYMOUS, T_REPLY } from '../../../../translations';
import {
  EQuestionState,
  IQnAQuestion,
  IQnAQuestionReply,
  TQnAQuestionReplies,
  IActionsMenuItem,
} from '../../../../types/interaction_tools';
import { stopEventPropagation } from '../../../../utils/dom/event';
import { createDomName } from '../../../../utils/dom/identifying';
import { CdnAvatar } from '../../../avatar';
import { BokehMenu, BokehMenuItem } from '../../../bokeh';

export interface IQuestionManageItemProps extends IBaseDomElementProps {
  question: IQnAQuestion;
  isActive: boolean;
  isPinned: boolean;
  itemOverlay?: ReactNode;
  children?: ReactNode;
  questionReplies?: TQnAQuestionReplies;
  menuItems?: Array<IActionsMenuItem>;
  onQuestionSelected?: (question: IQnAQuestion, element: HTMLDivElement) => void;
  onQuestionDeselected?: () => void;
  onQuestionArchive: (question: IQnAQuestion) => void;
  onQuestionUnArchive: (question: IQnAQuestion) => void;
  onQuestionApprove: (question: IQnAQuestion) => void;
  onQuestionPin: (questionId: string) => void;
  onQuestionUnPin: (questionId: string) => void;
  onDeleteQuestionReply: (questionId: string, replyId: string) => void;
}

export function NewQuestionManageItem({
  className = createDomName('question-manage-item'),
  question,
  isActive,
  isPinned,
  questionReplies = {},
  menuItems = [],
  onQuestionSelected,
  onQuestionDeselected,
  onQuestionArchive,
  onQuestionUnArchive,
  onQuestionApprove,
  onQuestionPin,
  onQuestionUnPin,
  onDeleteQuestionReply,
}: IQuestionManageItemProps) {
  const ref: Ref<HTMLDivElement> = useRef(null);
  const [isReplyVisible, setReplyVisible] = useState<boolean>(true);
  const { id: questionId, text, createdAt, anonymous, state, votesCount, user } = question;

  const { colorMode: themeName } = useColorMode();

  const { onMouseEnter, onMouseLeave, isMenuOpen, isItemHovered, onCloseMenu, onToggleMenu } =
    useSourceHoverControls();

  const timeAsked: string = useQuestionTimeAsked(createdAt);

  const name: string = anonymous || !user || !user.displayName ? T_ANONYMOUS : user.displayName;
  const avatar: ReactNode =
    anonymous || !user?.avatarUrl ? (
      <Avatar height={'sm'} width={'sm'} />
    ) : (
      <CdnAvatar size={'sm'} src={user.avatarUrl} name={name} />
    );
  const isPending: boolean = state === EQuestionState.PENDING;
  const isArchived: boolean = state === EQuestionState.ARCHIVED;
  const isVotesVisible: boolean = !isPending && votesCount > 0;
  const questionRepliesList: Array<IQnAQuestionReply> = Object.values(questionReplies);
  const hasReplies: boolean = questionRepliesList.length > 0;
  const canBePinned: boolean = !isPending && !isArchived;

  const itemClassName: string = useMemo(
    () => `${className} ${canBePinned && isPinned ? 'pinned-item' : ''}`,
    [className, canBePinned, isPinned],
  );

  const onQuestionClicked = useCallback(() => {
    if (isActive && onQuestionDeselected) {
      onQuestionDeselected();
      return;
    }
  }, [isActive, question, onQuestionSelected, onQuestionDeselected]);

  const onQuestionApproveClicked = useCallback(() => {
    if (onQuestionApprove && question) {
      onQuestionApprove(question);
    }
  }, [question, onQuestionApprove]);

  const onQuestionArchiveClicked = useCallback(() => {
    if (onQuestionArchive && question) {
      onQuestionArchive(question);
    }
  }, [question, onQuestionArchive]);

  const onQuestionUnArchiveClicked = useCallback(() => {
    if (onQuestionUnArchive && question) {
      onQuestionUnArchive(question);
    }
  }, [question, onQuestionUnArchive]);

  const onQuestionReplyClicked = useCallback(
    (event: MouseEvent) => {
      event.stopPropagation();

      if (onQuestionSelected && question) {
        onQuestionSelected(question, ref.current as HTMLDivElement);
      }
    },
    [onQuestionSelected, question],
  );

  const onQuestionReplyDeleteClicked = useCallback(
    (questionReplyId: string) => {
      trackDeleteQuestionReply();
      onDeleteQuestionReply(questionId, questionReplyId);
    },
    [questionId, onDeleteQuestionReply],
  );

  const onQuestionPinClicked = useCallback(
    (event: MouseEvent) => {
      event.stopPropagation();

      if (onQuestionPin && questionId) {
        onQuestionPin(questionId);
      }
    },
    [onQuestionPin, questionId],
  );

  const onQuestionUnPinClicked = useCallback(
    (event: MouseEvent) => {
      event.stopPropagation();

      if (isPinned && onQuestionUnPin && questionId) {
        onQuestionUnPin(questionId);
      }
    },
    [isPinned, onQuestionUnPin, questionId],
  );

  return (
    <Flex
      ref={ref}
      className={itemClassName}
      sx={createQuestionItemWrapperStyle({
        themeName,
        isPinned: canBePinned && isPinned,
        isHovered: isItemHovered,
      })}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onClick={onQuestionClicked}
    >
      {isItemHovered ? (
        <Flex
          className={createDomName(className, 'controls')}
          onMouseDown={stopEventPropagation}
          onTouchStart={stopEventPropagation}
          position={'absolute'}
          right={rem(8)}
          top={rem(8)}
          alignSelf={'flex-start'}
          lineHeight={rem(20)}
          gap={rem(2)}
        >
          {menuItems.length ? (
            <BokehMenu
              isOpen={isMenuOpen}
              placement={'bottom-end'}
              isInPortal={false}
              variant={'secondary'}
              onClose={onCloseMenu}
              onClick={onToggleMenu}
              menuList={
                <>
                  {menuItems.map(({ id, label, onClick }) => (
                    <BokehMenuItem
                      key={id}
                      className={createDomName(className, 'menu', 'item', 'id')}
                      onClick={(event) => {
                        onClick(event);
                        onCloseMenu();
                      }}
                    >
                      {label}
                    </BokehMenuItem>
                  ))}
                </>
              }
            />
          ) : null}

          <QuestionItemActions
            className={className}
            isPending={isPending}
            isArchived={isArchived}
            isPinned={isPinned}
            hasActiveMenu={Boolean(menuItems.length)}
            onQuestionArchiveClicked={onQuestionArchiveClicked}
            onQuestionUnArchiveClicked={onQuestionUnArchiveClicked}
            onQuestionApproveClicked={onQuestionApproveClicked}
            onQuestionUnPinClicked={onQuestionUnPinClicked}
            onQuestionPinClicked={onQuestionPinClicked}
          />
        </Flex>
      ) : null}

      {canBePinned && isPinned ? (
        <Flex
          display={isItemHovered ? 'none' : 'flex'}
          position={'absolute'}
          justifyContent={'center'}
          alignItems={'center'}
          alignSelf={'flex-start'}
          border={`${rem(1)} solid transparent`}
          height={rem(24)}
          minWidth={rem(24)}
          right={rem(8)}
          top={rem(8)}
        >
          <PinOnFilled boxSize={rem(16)} />
        </Flex>
      ) : null}

      <Box className={createDomName(className, 'author-avatar')} sx={createAvatarWrapperStyle()}>
        {avatar}
      </Box>

      <Flex
        flexDirection={'column'}
        alignItems={'flex-start'}
        width={'100%'}
        gap={rem(8)}
        overflow={'hidden'}
      >
        <Flex className={createDomName(className, 'author-info')} sx={createQuestionInfoStyle()}>
          <QuestionItemAuthor className={createDomName(className, 'author-name')} name={name} />

          <Text
            className={createDomName(className, 'time')}
            variant={'body-sm'}
            sx={TIME_ASKED_STYLE}
          >
            {timeAsked}
          </Text>
        </Flex>

        <Paragraph
          variant={'body-md'}
          className={createDomName(className, 'text')}
          sx={createQuestionTextStyle()}
        >
          {text}
        </Paragraph>

        <Flex columnGap={rem(8)} alignItems={'center'}>
          {hasReplies || isArchived ? null : (
            <Button
              className={createDomName(className, 'reply')}
              size={'xs'}
              variant={'secondary'}
              onClick={onQuestionReplyClicked}
            >
              {T_REPLY}
            </Button>
          )}

          {hasReplies ? (
            <Button
              size={'xs'}
              variant={'tertiary'}
              iconSpacing={rem(4)}
              rightIcon={isReplyVisible ? <ChevronUpSmall /> : <ChevronDownSmall />}
              onClick={(event) => {
                stopEventPropagation(event);
                setReplyVisible((v) => !v);
              }}
            >
              {T_REPLY}
            </Button>
          ) : null}

          {isVotesVisible ? (
            <QuestionItemVotesCount
              className={createDomName(className, 'votes')}
              votesCount={votesCount}
            />
          ) : null}
        </Flex>

        {questionRepliesList.length ? (
          <Box width={'100%'}>
            <Collapse in={isReplyVisible} animateOpacity>
              {questionRepliesList.map((it) => (
                <NewQuestionItemReply
                  key={it.id}
                  reply={it}
                  onDelete={() => onQuestionReplyDeleteClicked(it.id)}
                  isManagementAccessed={true}
                />
              ))}
            </Collapse>
          </Box>
        ) : null}
      </Flex>
    </Flex>
  );
}
